import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { componentTypes } from '../data/manufacturers';
import { products as initialProducts } from '../data/products';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from './ui/dialog';
import {
  Search,
  Package,
  Plus,
  Pencil,
  Trash2,
  X
} from 'lucide-react';

const GeraeteView = () => {
  const { addNode } = useProject();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('all');
  const [products, setProducts] = useState(initialProducts);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    id: '',
    name: '',
    manufacturer: '',
    category: '',
    nennstrom: '',
    charakteristik: '',
    pole: '',
    fehlerstrom: '',
    typ: '',
    querschnitt: '',
    kanaele: '',
    leistung: ''
  });

  const manufacturers = [
    { id: 'all', name: 'Alle Hersteller' },
    { id: 'hager', name: 'Hager' },
    { id: 'schrack', name: 'Schrack Technik' },
    { id: 'wago', name: 'WAGO' },
    { id: 'siemens', name: 'Siemens' },
    { id: 'eaton', name: 'Eaton' },
    { id: 'eltako', name: 'Eltako' },
    { id: 'homematic', name: 'Homematic IP' },
    { id: 'fronius', name: 'Fronius' },
    { id: 'shelly', name: 'Shelly' },
    { id: 'kostal', name: 'KOSTAL' },
    { id: 'e3dc', name: 'E3/DC' },
    { id: 'janitza', name: 'Janitza' },
    { id: 'spelsberg', name: 'Spelsberg' }
  ];

  const categories = [
    { id: 'sls', name: 'SLS-Schalter' },
    { id: 'ls', name: 'Leitungsschutzschalter' },
    { id: 'rcd', name: 'RCD/FI-Schalter' },
    { id: 'rcbo', name: 'RCBO/FI-LS' },
    { id: 'schuetz', name: 'Schütz' },
    { id: 'motorschutz', name: 'Motorschutzschalter' },
    { id: 'durchgangsklemme', name: 'Durchgangsklemme' },
    { id: 'etagenklemme', name: 'Etagenklemme' },
    { id: 'verbindungsklemme', name: 'Verbindungsklemme' },
    { id: 'dimmer', name: 'Dimmer' },
    { id: 'stromstoss', name: 'Stromstoßschalter' },
    { id: 'treppenlicht', name: 'Treppenhausschalter' },
    { id: 'wired', name: 'HomematicIP Wired' },
    { id: 'funk', name: 'HomematicIP Funk' },
    { id: 'm22', name: 'Schalttafelelemente' },
    { id: 'logo', name: 'Steuerung/Logo' },
    { id: 'sammelschiene', name: 'Sammelschiene' },
    { id: 'verteiler', name: 'Verteiler' },
    { id: 'smartmeter', name: 'SmartMeter' },
    { id: 'general', name: 'Allgemein' }
  ];

  // Alle Produkte flach auflisten
  const getAllProducts = () => {
    const allProducts = [];
    
    Object.entries(products).forEach(([manufacturer, categories]) => {
      if (selectedManufacturer !== 'all' && manufacturer !== selectedManufacturer) return;
      
      if (Array.isArray(categories)) {
        categories.forEach(product => {
          allProducts.push({
            ...product,
            manufacturer,
            category: 'general'
          });
        });
      } else {
        Object.entries(categories).forEach(([category, productList]) => {
          if (Array.isArray(productList)) {
            productList.forEach(product => {
              allProducts.push({
                ...product,
                manufacturer,
                category
              });
            });
          }
        });
      }
    });

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return allProducts.filter(p => 
        p.name?.toLowerCase().includes(term) ||
        p.id?.toLowerCase().includes(term) ||
        p.manufacturer?.toLowerCase().includes(term)
      );
    }

    return allProducts;
  };

  const filteredProducts = getAllProducts();

  const handleAddProduct = (product) => {
    const categoryToType = {
      sls: 'sls',
      ls: 'ls',
      rcd: 'rcd',
      rcbo: 'rcbo',
      schuetz: 'schuetz',
      motorschutz: 'motorschutz',
      durchgangsklemme: 'klemme',
      etagenklemme: 'klemme',
      verbindungsklemme: 'klemme',
      logo: 'logo',
      dimmer: 'dimmer',
      stromstoss: 'stromstoss',
      treppenlicht: 'treppenlicht',
      wired: 'homematicAktor',
      funk: 'homematicAktor',
      m22: 'taster',
      sammelschiene: 'sammelschiene',
      verteiler: 'verteiler',
      smartmeter: 'smartMeter'
    };

    const nodeType = categoryToType[product.category] || 'ls';
    
    addNode(null, nodeType, {
      bezeichnung: product.name,
      hersteller: product.manufacturer,
      artikelnummer: product.id,
      ...product
    });
  };

  const handleCreateProduct = () => {
    if (!newProduct.id || !newProduct.name || !newProduct.manufacturer || !newProduct.category) {
      alert('Bitte füllen Sie alle Pflichtfelder aus (Artikelnummer, Name, Hersteller, Kategorie)');
      return;
    }

    const updatedProducts = { ...products };
    const manufacturer = newProduct.manufacturer;
    const category = newProduct.category;

    // Produkt-Objekt erstellen (nur gefüllte Felder)
    const productData = { id: newProduct.id, name: newProduct.name };
    if (newProduct.nennstrom) productData.nennstrom = newProduct.nennstrom;
    if (newProduct.charakteristik) productData.charakteristik = newProduct.charakteristik;
    if (newProduct.pole) productData.pole = newProduct.pole;
    if (newProduct.fehlerstrom) productData.fehlerstrom = newProduct.fehlerstrom;
    if (newProduct.typ) productData.typ = newProduct.typ;
    if (newProduct.querschnitt) productData.querschnitt = newProduct.querschnitt;
    if (newProduct.kanaele) productData.kanaele = newProduct.kanaele;
    if (newProduct.leistung) productData.leistung = newProduct.leistung;

    // Hersteller existiert nicht
    if (!updatedProducts[manufacturer]) {
      updatedProducts[manufacturer] = {};
    }

    // Kategorie als Array oder Objekt
    if (Array.isArray(updatedProducts[manufacturer])) {
      updatedProducts[manufacturer].push(productData);
    } else {
      if (!updatedProducts[manufacturer][category]) {
        updatedProducts[manufacturer][category] = [];
      }
      updatedProducts[manufacturer][category].push(productData);
    }

    setProducts(updatedProducts);
    setNewProduct({
      id: '', name: '', manufacturer: '', category: '',
      nennstrom: '', charakteristik: '', pole: '', fehlerstrom: '',
      typ: '', querschnitt: '', kanaele: '', leistung: ''
    });
    setIsAddDialogOpen(false);
  };

  const handleDeleteProduct = (product) => {
    if (!window.confirm(`Produkt "${product.name}" (${product.id}) wirklich löschen?`)) return;

    const updatedProducts = { ...products };
    const manufacturer = product.manufacturer;
    const category = product.category;

    if (Array.isArray(updatedProducts[manufacturer])) {
      updatedProducts[manufacturer] = updatedProducts[manufacturer].filter(p => p.id !== product.id);
    } else if (updatedProducts[manufacturer][category]) {
      updatedProducts[manufacturer][category] = updatedProducts[manufacturer][category].filter(p => p.id !== product.id);
    }

    setProducts(updatedProducts);
  };

  const getManufacturerColor = (manufacturer) => {
    const colors = {
      hager: 'bg-green-100 text-green-800',
      schrack: 'bg-blue-100 text-blue-800',
      wago: 'bg-orange-100 text-orange-800',
      siemens: 'bg-cyan-100 text-cyan-800',
      eaton: 'bg-red-100 text-red-800',
      eltako: 'bg-purple-100 text-purple-800',
      homematic: 'bg-emerald-100 text-emerald-800',
      fronius: 'bg-yellow-100 text-yellow-800',
      shelly: 'bg-indigo-100 text-indigo-800',
      kostal: 'bg-lime-100 text-lime-800',
      e3dc: 'bg-teal-100 text-teal-800',
      janitza: 'bg-pink-100 text-pink-800',
      spelsberg: 'bg-gray-100 text-gray-800'
    };
    return colors[manufacturer] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="h-full overflow-hidden bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Produkte suchen..."
              className="pl-10"
            />
          </div>
          
          {/* Neues Produkt Button */}
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Neues Produkt
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Neues Produkt anlegen</DialogTitle>
                <DialogDescription>
                  Fügen Sie ein neues Produkt zur Datenbank hinzu.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="space-y-2">
                  <Label>Hersteller *</Label>
                  <Select
                    value={newProduct.manufacturer}
                    onValueChange={(value) => setNewProduct(prev => ({ ...prev, manufacturer: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {manufacturers.filter(m => m.id !== 'all').map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Kategorie *</Label>
                  <Select
                    value={newProduct.category}
                    onValueChange={(value) => setNewProduct(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Artikelnummer *</Label>
                  <Input
                    value={newProduct.id}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, id: e.target.value }))}
                    placeholder="z.B. MBN116"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Produktname *</Label>
                  <Input
                    value={newProduct.name}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. LS-Schalter 1P B16A"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nennstrom (A)</Label>
                  <Input
                    value={newProduct.nennstrom}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, nennstrom: e.target.value }))}
                    placeholder="z.B. 16"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Charakteristik</Label>
                  <Input
                    value={newProduct.charakteristik}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, charakteristik: e.target.value }))}
                    placeholder="z.B. B, C, D"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Polzahl</Label>
                  <Input
                    value={newProduct.pole}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, pole: e.target.value }))}
                    placeholder="z.B. 1, 3, 4"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fehlerstrom (mA)</Label>
                  <Input
                    value={newProduct.fehlerstrom}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, fehlerstrom: e.target.value }))}
                    placeholder="z.B. 30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Typ</Label>
                  <Input
                    value={newProduct.typ}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, typ: e.target.value }))}
                    placeholder="z.B. A, F, B"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kanäle</Label>
                  <Input
                    value={newProduct.kanaele}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, kanaele: e.target.value }))}
                    placeholder="z.B. 4"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleCreateProduct} className="bg-emerald-600 hover:bg-emerald-700">
                  Produkt anlegen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Hersteller Filter */}
        <div className="flex gap-2 overflow-x-auto pb-1 mt-4">
          {manufacturers.map((m) => (
            <Button
              key={m.id}
              variant={selectedManufacturer === m.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedManufacturer(m.id)}
              className={`whitespace-nowrap ${selectedManufacturer === m.id ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
            >
              {m.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Product Grid */}
      <ScrollArea className="flex-1 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product, index) => (
            <Card key={`${product.manufacturer}-${product.id}-${index}`} className="hover:shadow-md transition-shadow group">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm font-medium truncate">{product.name}</CardTitle>
                    <CardDescription className="font-mono text-xs">{product.id}</CardDescription>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge className={`${getManufacturerColor(product.manufacturer)} text-xs`}>
                      {product.manufacturer}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteProduct(product)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-gray-500 space-y-1 mb-3">
                  {product.nennstrom && <div>Nennstrom: {product.nennstrom}A</div>}
                  {product.charakteristik && <div>Charakteristik: {product.charakteristik}</div>}
                  {product.pole && <div>Pole: {product.pole}</div>}
                  {product.fehlerstrom && <div>Fehlerstrom: {product.fehlerstrom}mA</div>}
                  {product.typ && <div>Typ: {product.typ}</div>}
                  {product.querschnitt && <div>Querschnitt: {product.querschnitt}mm²</div>}
                  {product.kanaele && <div>Kanäle: {product.kanaele}</div>}
                  {product.leistung && <div>Leistung: {product.leistung}W</div>}
                  {product.messart && <div>Messart: {product.messart}</div>}
                </div>
                <Button
                  size="sm"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleAddProduct(product)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Zum Projekt hinzufügen
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        {filteredProducts.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Keine Produkte gefunden</p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default GeraeteView;
